import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { HiArrowSmRight, HiCheckCircle, HiChevronDown, HiMoon, HiOutlineCalendar, HiSun } from "react-icons/hi";
import LogOutModal from "../models/logOutModel";
import { getUserProfile } from "../../controllers/utilityController";

import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "./breadcrumbs";
import { getPageMeta } from "../../routes/breadcrumbs";
import { usePageTitleOverride } from "../layouts/PageTitleContext";
import InstallAppButton from "./installAppButton";
import ChatHeaderButton from "../chat/ChatHeaderButton";
import TourMenuButton from "../../tours/TourMenuButton";
import UserAvatar from "./UserAvatar";
import { signOut } from "../../services/signOut";
import { useChat } from "../chat/ChatProvider";
import { useIsMobile } from "../chat/useMedia";
import MobileAppBar from "./MobileNav";

const APP_NAME = "IRG Enterprise System";

function HeaderComponent() {
  const [dateTime, setDateTime] = useState({ date: "", time: "" });
  const [greeting, setGreeting] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const dropdownRef = useRef(null);
  const [profile, setProfile] = useState({ id: null, name: "", role: "", avatarThumbUrl: null });

  useEffect(() => {
    getUserProfile()
      .then((res) => setProfile({ id: res.id, name: res.name || "", role: res.role || "", avatarThumbUrl: res.avatarThumbUrl || null }))
      .catch(() => {});
  }, []);

  // Name and photo update live (profile edits, HR linking) through the chat connection.
  const liveMe = useChat()?.me;

  const { pathname } = useLocation();
  const titleOverride = usePageTitleOverride();
  const { title: pageTitle, crumbs } = getPageMeta(pathname, { role: profile.role, titleOverride });

  useEffect(() => {
    document.title = pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME;
  }, [pageTitle]);

  const displayName = liveMe?.name || profile.name || "User";
  const avatarSrc = liveMe ? liveMe.avatarThumbUrl : profile.avatarThumbUrl;
  const displayRole = profile.role
    ? profile.role === "user"
      ? "Employee"
      : profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
    : "";

  // Handle dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const navigate = useNavigate();
  const mobile = useIsMobile();

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const hours = now.getHours();
      setGreeting(
        hours < 12
          ? "Good morning 🌤️"
          : hours < 18
            ? "Good afternoon ☀️"
            : "Good evening 🌙"
      );
      setDateTime({
        date: now.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        time: now.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hourCycle: "h23",
        }),
      });
    };
    updateClock();
    const timer = setInterval(updateClock, 1000);
    return () => clearInterval(timer);
  }, []);


  // 🔒 Logout
  const handleShowLogoutModal = () => setIsLogoutModalVisible(true);
  const handleCloseLogoutModal = () => setIsLogoutModalVisible(false);
  const handleConfirmLogout = () => signOut();

  // 🧠 Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 260, damping: 20 },
    },
    exit: { opacity: 0, y: -6, scale: 0.98, transition: { duration: 0.15 } },
  };

  // Phones get a native-style app bar with a drop-down menu instead of the sidebar + this header.
  if (mobile) {
    return (
      <MobileAppBar
        title={pageTitle}
        profile={{ id: profile.id || liveMe?.id, name: displayName, avatarSrc, role: displayRole, rawRole: profile.role }}
        isDarkMode={isDarkMode}
        onToggleTheme={() => setIsDarkMode((v) => !v)}
        onSignOut={handleConfirmLogout}
      />
    );
  }

  return (
    <>
      <motion.header
        className="sticky top-0 z-40 h-[72px] bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200/80 dark:border-gray-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)] flex items-center justify-between gap-4 px-4 sm:px-6"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 120, damping: 18 }}
      >
        {/* LEFT: Breadcrumbs + Page title */}
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
          <Breadcrumbs items={crumbs} />
          <h1 className="truncate text-lg font-semibold leading-tight text-app-heading sm:text-xl">
            {pageTitle}
          </h1>
        </div>

        {/* RIGHT: Date/Time pill + Theme toggle + Divider + User menu */}
        <div className="flex shrink-0 items-center gap-2 sm:gap-3" ref={dropdownRef}>
          <div className="hidden xl:flex items-center h-10 px-4 select-none rounded-full shadow-sm">
            <HiOutlineCalendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="ml-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              {dateTime.date}
            </span>
            <span className="mx-3 h-4 w-px bg-gray-300 dark:bg-gray-600" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums tracking-wide">
              {dateTime.time}
            </span>
          </div>

          <InstallAppButton />

          <ChatHeaderButton />

          <TourMenuButton />

          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsDarkMode(!isDarkMode)}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            data-tour="header-theme"
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 dark:bg-gray-800 border border-amber-100 dark:border-gray-700 shadow-sm hover:bg-amber-100 dark:hover:bg-gray-700 transition-colors"
          >
            <motion.div
              initial={false}
              animate={{ rotate: isDarkMode ? 180 : 0 }}
              transition={{ duration: 0.5 }}
            >
              {isDarkMode ? (
                <HiMoon className="w-5 h-5 text-indigo-400" />
              ) : (
                <HiSun className="w-5 h-5 text-orange-500" />
              )}
            </motion.div>
          </motion.button>

          <span className="mx-1 hidden h-8 w-px bg-gray-200 dark:bg-gray-700 sm:block" />

          <div className="relative">
            <button
              type="button"
              onClick={() => setDropdownOpen((prev) => !prev)}
              data-tour="header-profile-menu"
              className="flex items-center gap-3 pl-1 pr-2 py-1 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <UserAvatar name={displayName} src={avatarSrc} seed={profile.id || liveMe?.id} size="md" className="shadow-sm ring-2 ring-white dark:ring-gray-900 rounded-full" />

              <div className="hidden md:flex flex-col items-start leading-tight text-left">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 max-w-[160px] truncate">
                  {displayName}
                </span>
                {displayRole && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {displayRole}
                  </span>
                )}
              </div>

              <motion.div
                className="ml-1 hidden md:block"
                animate={{ rotate: dropdownOpen ? 180 : 0 }}
                transition={{ duration: 0.25 }}
              >
                <HiChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </motion.div>
            </button>

            {/* Animated Dropdown */}
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  key="dropdown"
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="absolute right-0 mt-2 w-64 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border border-gray-100 dark:border-gray-700 rounded-xl shadow-xl py-3 "
                >
                  <div className="px-4 pb-3 mb-2 border-b border-gray-100 dark:border-gray-700 select-none">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                      {greeting}, <span className="text-indigo-600 dark:text-indigo-400">{displayName}</span>
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <HiCheckCircle className="text-green-500" /> System Operational
                    </div>
                  </div>
                  <div className="px-4 pb-2 border-b border-gray-100 dark:border-gray-700 select-none">
                    <button
                      className="w-full text-left p-2 rounded-md text-gray-700 dark:text-gray-200 transition-colors hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:active:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        setDropdownOpen(false);
                        navigate("/profile");
                      }}
                      data-tour="header-my-profile"
                    >
                      My Profile
                    </button>
                    <button
                      className="w-full text-left p-2 rounded-md text-gray-700 dark:text-gray-200 transition-colors hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:active:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => alert("Settings clicked")}
                    >
                      Settings
                    </button>
                  </div>

                  <div className="p-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleConfirmLogout}
                      className="w-full flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#2470B7] to-[#1d5a93] text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                    >
                      <HiArrowSmRight className="h-5 w-5" />
                      Sign Out
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.header>
      <LogOutModal
        show={isLogoutModalVisible}
        handleClose={handleCloseLogoutModal}
        handleConfirm={handleConfirmLogout}
      />
    </>
  );
}

export default HeaderComponent;
